import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { communityService, invitationService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../hooks/useAuth';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';

const COMMUNITY_COLORS = ['bg-primary-container', 'bg-secondary-container', 'bg-surface-container-highest', 'bg-primary-container/60'];
const COMMUNITY_ICONS = ['group_work', 'school', 'palette', 'hub', 'apartment', 'store'];
const MEMBER_BG_COLORS = ['bg-primary', 'bg-secondary', 'bg-tertiary'];
const MEMBER_TEXT_COLORS = ['text-on-primary', 'text-on-secondary', 'text-on-tertiary'];

/* ═══════════════════════════════════════════════════════════════════════
   Card style presets — one preset per variant. Each tweaks the wrapper
   classes (bg / shadow / border) and optionally overrides the page bg
   so the cards land on a different surface.
   ═══════════════════════════════════════════════════════════════════════ */
/* Final preset — gray rgb(225) #e1e1e1. Class names inlined para que Tailwind
   JIT los detecte al build time. */
const CARD_PRESET = {
  cardClass: 'bg-[#e1e1e1] hover:bg-[#d5d5d5] shadow-sm hover:shadow-lg border border-[#d5d5d5]',
  statsBg: 'bg-white/85',
  createCardClass: 'border-4 border-dashed border-[#e1e1e1] bg-white hover:bg-[#fafafa]',
};

/* ═══════════════════════════════════════════════════════════════════════
   Community Card — single component, preset-driven
   Stats v2 layout (members + Activas / Vendidas / Reservadas).
   ═══════════════════════════════════════════════════════════════════════ */
function CommunityCard({ community: c, index: i, onClick, preset, dense = false, avatarMode = false }) {
  const memberCount = c.memberCount ?? 0;
  const active = c.activeListings ?? 0;
  const sold = c.soldListings ?? 0;
  const reserved = c.reservedListings ?? 0;
  const sampleIds = Array.isArray(c.sampleMemberIds) ? c.sampleMemberIds : [];
  const sampleImages = Array.isArray(c.sampleMemberImageIds) ? c.sampleMemberImageIds : [];

  /* ── Variante densa (mobile 2-col): tile cuadrado estilo app icon ───────
     Las cards a ~170 px no bancan ni descripción ni 3-stats con labels largos.
     Diseño: cuadrado (aspect-square), ícono grande centrado, nombre debajo
     a 2 líneas máx, contador de miembros chiquito al pie. Sin números de
     listings — esa info vive en /communities (la versión completa). */
  if (dense) {
    return (
      <div
        onClick={onClick}
        className={`${preset.cardClass} aspect-square p-3 rounded-xl transition-all duration-300 group cursor-pointer flex flex-col items-center justify-center text-center gap-2`}
      >
        <div className={`w-14 h-14 rounded-2xl ${c.imageId ? 'bg-surface' : COMMUNITY_COLORS[i % COMMUNITY_COLORS.length]} flex items-center justify-center ${c.imageId ? '' : 'shadow-inner'} overflow-hidden shrink-0`}>
          {c.imageId ? (
            <img alt={c.name} className="w-full h-full object-cover" src={getImageUrl(c.imageId)} />
          ) : (
            <span className="material-symbols-outlined text-2xl text-on-surface-variant">
              {COMMUNITY_ICONS[i % COMMUNITY_ICONS.length]}
            </span>
          )}
        </div>
        <h4 className="text-sm font-bold text-on-surface line-clamp-2 leading-tight px-1 break-words">
          {c.name}
        </h4>
        {avatarMode ? (
          <div className="flex -space-x-2 justify-center">
            {sampleIds.slice(0, 3).map((uid, j) => {
              const img = sampleImages[j];
              const colorIdx = ((Number(uid) || j) + i) % MEMBER_BG_COLORS.length;
              return (
                <div
                  key={uid ?? j}
                  className={`w-6 h-6 rounded-full ring-2 ring-surface shadow-sm overflow-hidden flex items-center justify-center ${img ? 'bg-surface-container-highest' : MEMBER_BG_COLORS[colorIdx]}`}
                >
                  {img ? (
                    <img alt="" className="w-full h-full object-cover" src={getImageUrl(img)} />
                  ) : (
                    <span className={`material-symbols-outlined text-[12px] ${MEMBER_TEXT_COLORS[colorIdx]}`}>person</span>
                  )}
                </div>
              );
            })}
            {memberCount > sampleIds.length && (
              <div className="w-6 h-6 rounded-full ring-2 ring-surface shadow-sm bg-surface-container-highest flex items-center justify-center text-[9px] font-bold text-on-surface">
                +{memberCount - sampleIds.length}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1 text-[10px] font-bold text-on-surface-variant">
            <span className="material-symbols-outlined text-[12px]">person</span>
            {memberCount}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className={`${preset.cardClass} p-8 rounded-xl transition-all duration-300 group cursor-pointer`}
    >
      <div className="flex justify-between items-start mb-6">
        <div className={`w-16 h-16 rounded-2xl ${c.imageId ? 'bg-surface' : COMMUNITY_COLORS[i % COMMUNITY_COLORS.length]} flex items-center justify-center ${c.imageId ? '' : 'shadow-inner'} overflow-hidden`}>
          {c.imageId ? (
            <img alt={c.name} className="w-full h-full object-cover" src={getImageUrl(c.imageId)} />
          ) : (
            <span className="material-symbols-outlined text-2xl text-on-surface-variant">
              {COMMUNITY_ICONS[i % COMMUNITY_ICONS.length]}
            </span>
          )}
        </div>
        <div className="flex -space-x-3">
          {sampleIds.slice(0, 3).map((uid, j) => {
            const img = sampleImages[j];
            const colorIdx = ((Number(uid) || j) + i) % MEMBER_BG_COLORS.length;
            return (
              <div
                key={uid ?? j}
                className={`w-8 h-8 rounded-full ring-2 ring-surface shadow-sm overflow-hidden flex items-center justify-center ${img ? 'bg-surface-container-highest' : MEMBER_BG_COLORS[colorIdx]}`}
                title={`Miembro #${uid ?? '?'}`}
              >
                {img ? (
                  <img alt="" className="w-full h-full object-cover" src={getImageUrl(img)} />
                ) : (
                  <span className={`material-symbols-outlined text-sm ${MEMBER_TEXT_COLORS[colorIdx]}`}>person</span>
                )}
              </div>
            );
          })}
          {memberCount > sampleIds.length && (
            <div className="w-8 h-8 rounded-full ring-2 ring-surface shadow-sm bg-surface-container-highest flex items-center justify-center text-[10px] font-bold text-on-surface">
              +{memberCount - sampleIds.length}
            </div>
          )}
        </div>
      </div>

      <h4 className="text-xl font-bold text-on-surface mb-2 truncate">{c.name}</h4>

      <p className="text-xs text-on-surface-variant italic mb-5 line-clamp-2 min-h-[1.5rem]">
        {c.description ? `"${c.description}"` : ' '}
      </p>

      <div className={`grid grid-cols-3 gap-2 ${preset.statsBg} p-3 rounded-lg`}>
        <div className="flex flex-col items-center">
          <span className="text-lg font-black text-on-surface leading-none">{active}</span>
          <span className="text-[9px] uppercase tracking-widest font-bold text-on-surface-variant mt-1">Activas</span>
        </div>
        <div className="flex flex-col items-center border-x border-outline-variant/30">
          <span className="text-lg font-black text-primary leading-none">{sold}</span>
          <span className="text-[9px] uppercase tracking-widest font-bold text-on-surface-variant mt-1">Vendidas</span>
        </div>
        <div className="flex flex-col items-center">
          <span className="text-lg font-black text-on-surface leading-none">{reserved}</span>
          <span className="text-[9px] uppercase tracking-widest font-bold text-on-surface-variant mt-1">Reservadas</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Invitation card — mismo tamaño visual que CommunityCard. Tinted con
   gradiente secondary→primary para diferenciarla. La acción primaria
   (Accept) y secundaria (Decline) van apiladas al pie ocupando todo el
   ancho, siguiendo la grilla del bloque de stats de CommunityCard.
   ═══════════════════════════════════════════════════════════════════════ */
function InvitationCard({ inv, acceptMutation, rejectMutation, dense = false }) {
  const { t } = useTranslation();

  if (dense) {
    return (
      <div className="bg-gradient-to-br from-secondary-container/50 to-primary-container/30 border border-secondary/30 aspect-square p-3 rounded-xl flex flex-col items-center justify-center text-center gap-2 shadow-sm hover:shadow-md transition-shadow">
        <div className="w-14 h-14 rounded-2xl bg-secondary-container flex items-center justify-center text-secondary shadow-inner shrink-0">
          <span className="material-symbols-outlined text-2xl">mail</span>
        </div>
        <h4 className="text-sm font-bold text-on-surface line-clamp-2 leading-tight px-1 break-words">
          {inv.communityName}
        </h4>
        <div className="flex gap-1 w-full mt-1">
          <a onClick={() => acceptMutation.mutate(inv.token)} className="flex-1 py-1 bg-primary text-on-primary rounded-md text-[10px] font-bold cursor-pointer text-center">
            {t('Accept')}
          </a>
          <a onClick={() => rejectMutation.mutate(inv.token)} className="flex-1 py-1 bg-surface-container-highest text-on-surface-variant rounded-md text-[10px] font-bold cursor-pointer text-center">
            {t('Decline')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-secondary-container/50 to-primary-container/30 border border-secondary/30 p-8 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
      <div className="flex justify-between items-start mb-6">
        <div className="w-16 h-16 rounded-2xl bg-secondary-container flex items-center justify-center text-secondary shadow-inner">
          <span className="material-symbols-outlined text-2xl">mail</span>
        </div>
      </div>

      <h4 className="text-xl font-bold text-on-surface mb-2 truncate">{inv.communityName}</h4>

      <p className="text-xs text-on-surface-variant italic mb-5 line-clamp-2 min-h-[1.5rem]">
        {inv.inviterName
          ? t('{{inviter}} invited you to', { inviter: inv.inviterName })
          : t('You were invited to')}
        {inv.message ? ` — "${inv.message}"` : ''}
      </p>

      <div className="grid grid-cols-2 gap-2">
        <a onClick={() => acceptMutation.mutate(inv.token)} className="py-2 bg-primary text-on-primary rounded-lg text-sm font-bold hover:opacity-90 transition-all cursor-pointer text-center">
          {t('Accept')}
        </a>
        <a onClick={() => rejectMutation.mutate(inv.token)} className="py-2 bg-surface-container-highest text-on-surface-variant rounded-lg text-sm font-bold hover:bg-surface-variant transition-all cursor-pointer text-center">
          {t('Decline')}
        </a>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Available-by-email card — mismo tamaño que CommunityCard. La comunidad
   es elegible por dominio del mail del user; un click hace join directo.
   ═══════════════════════════════════════════════════════════════════════ */
function AvailableCommunityCard({ c, joinMutation, preset, dense = false }) {
  const { t } = useTranslation();

  if (dense) {
    return (
      <div
        onClick={() => joinMutation.mutate(c.id)}
        className={`${preset.cardClass} aspect-square p-3 rounded-xl transition-all duration-300 cursor-pointer flex flex-col items-center justify-center text-center gap-2`}
      >
        <div className={`w-14 h-14 rounded-2xl ${c.imageId ? 'bg-surface' : 'bg-secondary-container'} flex items-center justify-center text-secondary ${c.imageId ? '' : 'shadow-inner'} overflow-hidden shrink-0`}>
          {c.imageId ? (
            <img alt={c.name} className="w-full h-full object-cover" src={getImageUrl(c.imageId)} />
          ) : (
            <span className="material-symbols-outlined text-2xl">school</span>
          )}
        </div>
        <h4 className="text-sm font-bold text-on-surface line-clamp-2 leading-tight px-1 break-words">
          {c.name}
        </h4>
        <div className="text-[10px] font-bold text-secondary">{t('Join Community')}</div>
      </div>
    );
  }

  return (
    <div className={`${preset.cardClass} p-8 rounded-xl transition-all duration-300 group`}>
      <div className="flex justify-between items-start mb-6">
        <div className={`w-16 h-16 rounded-2xl ${c.imageId ? 'bg-surface' : 'bg-secondary-container'} flex items-center justify-center text-secondary ${c.imageId ? '' : 'shadow-inner'} overflow-hidden`}>
          {c.imageId ? (
            <img alt={c.name} className="w-full h-full object-cover" src={getImageUrl(c.imageId)} />
          ) : (
            <span className="material-symbols-outlined text-2xl">school</span>
          )}
        </div>
      </div>

      <h4 className="text-xl font-bold text-on-surface mb-2 truncate">{c.name}</h4>

      <p className="text-xs text-on-surface-variant italic mb-5 line-clamp-2 min-h-[1.5rem]">
        {c.domain ? (
          <>
            {t('Available via your')} <span className="font-semibold text-secondary">@{c.domain}</span> {t('email')}
          </>
        ) : ' '}
      </p>

      <a onClick={() => joinMutation.mutate(c.id)} className="block w-full py-2 border-2 border-secondary/30 text-secondary rounded-lg text-sm font-bold hover:bg-secondary hover:text-white transition-all cursor-pointer text-center">
        {t('Join Community')}
      </a>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Create-community card — diseño original (icono central + título +
   descripción, todo centrado). El min-h-[290px] iguala el alto natural
   de CommunityCard cuando aparece junto a ella (caso left-panel split,
   donde el grid no estira las cards entre sí).
   ═══════════════════════════════════════════════════════════════════════ */
function CreateCommunityCard({ navigate, preset, dense = false }) {
  const { t } = useTranslation();
  return (
    <div
      onClick={() => navigate('/communities/create')}
      className={`${preset.createCardClass} ${dense ? 'aspect-square p-3 gap-2' : 'p-8 min-h-[290px]'} rounded-xl flex flex-col items-center justify-center text-center group transition-all duration-300 cursor-pointer`}
    >
      <div className={`${dense ? 'w-14 h-14 rounded-2xl' : 'w-16 h-16 mb-6 rounded-full'} bg-surface-container-highest flex items-center justify-center group-hover:scale-110 transition-transform`}>
        <span className={`material-symbols-outlined text-primary ${dense ? 'text-2xl' : 'text-3xl'}`}>add</span>
      </div>
      <h4 className={`${dense ? 'text-sm leading-tight px-1' : 'text-xl mb-2'} font-bold text-on-surface`}>
        {t('Start a New Community')}
      </h4>
      {!dense && (
        <p className="text-xs text-on-surface-variant max-w-[180px]">
          {t('Bring your friends and fellow members together.')}
        </p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════════════ */
function Communities({ dense = false, avatarMode = false }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = getThemeVars(ACTIVE_THEME);
  const preset = CARD_PRESET;

  // The api.js response interceptor already unwraps Spring's DataResponse,
  // so r.data is the inner array directly (NOT r.data.data — that returns undefined).
  const { data: communities = [], isLoading: communitiesLoading } = useQuery({
    queryKey: ['userCommunities'],
    queryFn: async () => { const r = await communityService.getUserCommunities(); return r.data || []; },
  });

  const { data: invitations = [] } = useQuery({
    queryKey: ['pendingInvitations'],
    queryFn: async () => { const r = await invitationService.getPending(); return r.data || []; },
  });

  const { data: availableCommunities = [] } = useQuery({
    queryKey: ['availableCommunities'],
    queryFn: async () => { const r = await communityService.getAvailableToJoin(); return r.data || []; },
  });

  // Cuando cambia la membresía de comunidades del user, hay que invalidar
  // también el feed de la home (productCards) y la actividad reciente —
  // si no, al volver al dashboard se ven los productos cacheados sin los
  // de la comunidad recién unida y hay que refrescar a mano.
  const invalidateMembershipDependentQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['userCommunities'] });
    queryClient.invalidateQueries({ queryKey: ['availableCommunities'] });
    queryClient.invalidateQueries({ queryKey: ['productCards'] });
    queryClient.invalidateQueries({ queryKey: ['recentActivity'] });
  };

  const acceptMutation = useMutation({
    mutationFn: (token) => invitationService.acceptByToken(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingInvitations'] });
      invalidateMembershipDependentQueries();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (token) => invitationService.rejectByToken(token),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['pendingInvitations'] }); },
  });

  const joinMutation = useMutation({
    mutationFn: (communityId) => communityService.joinCommunity(communityId, user?.id),
    onSuccess: () => { invalidateMembershipDependentQueries(); },
  });

  const LoadingSkeleton = ({ cols = 'grid-cols-1 mobile-sm:grid-cols-2 gap-6' }) => (
    <div className={`grid ${cols}`}>
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="bg-surface-container p-8 rounded-xl shadow-sm animate-pulse">
          <div className="flex justify-between items-start mb-6">
            <div className="w-16 h-16 rounded-2xl bg-surface-container-high" />
            <div className="flex -space-x-3">
              <div className="w-8 h-8 rounded-full bg-surface-container-high" />
              <div className="w-8 h-8 rounded-full bg-surface-container-high" />
            </div>
          </div>
          <div className="h-5 bg-surface-container-high rounded w-3/4 mb-4" />
          <div className="h-3 bg-surface-container-high rounded w-1/2" />
        </div>
      ))}
    </div>
  );

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <span className="material-symbols-outlined text-6xl text-outline-variant mb-4">groups</span>
      <h3 className="text-lg font-bold text-on-surface mb-2">{t('No communities yet')}</h3>
      <p className="text-sm text-on-surface-variant mb-6 max-w-md">{t('Si tu institución ya tiene una comunidad en Vatuco, podés unirte agregando el mail de la institución a tu cuenta.')}</p>
      <div className="flex flex-col mobile-sm:flex-row gap-2 items-center">
        <a
          onClick={() => navigate('/profile?addEmail=1')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-on-primary font-medium hover:opacity-90 transition-opacity cursor-pointer"
        >
          <span className="material-symbols-outlined text-base">mail</span>
          {t('Agregar mail institucional')}
        </a>
        <a
          onClick={() => navigate('/communities/create')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-outline text-on-surface font-medium hover:bg-surface-container transition-colors cursor-pointer"
        >
          <span className="material-symbols-outlined text-base">add</span>
          {t('Create Community')}
        </a>
      </div>
    </div>
  );

  const HeaderRow = () => (
    <div className="flex items-center justify-between mb-6">
      <h3 className="text-xl font-bold text-on-surface flex items-center gap-2">
        <span className="material-symbols-outlined text-primary">hub</span>
        {t("Your Communities")}
      </h3>
      {communities.length > 0 && (
        <span className="bg-surface-container-highest px-3 py-1 rounded-full text-xs font-bold text-on-surface-variant">{communities.length}</span>
      )}
    </div>
  );

  const hasLeftPanel = invitations.length > 0 || availableCommunities.length > 0;

  const sideGridClass = dense
    ? 'grid grid-cols-2 gap-3'
    : 'grid grid-cols-1 mobile-sm:grid-cols-2 gap-6';
  const fullGridClass = dense
    ? 'grid grid-cols-2 mobile-sm:grid-cols-2 tablet-lg:grid-cols-4 gap-3'
    : 'grid grid-cols-1 mobile-sm:grid-cols-2 tablet-lg:grid-cols-3 gap-6';
  const pageBg = preset.pageBg || 'var(--chrome-bg)';

  return (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
      style={{ ...theme, backgroundColor: pageBg, color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar activePath="/communities" />
      <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
        <div className="px-6 tablet-sm:px-12 pt-8 pb-20 max-w-7xl mx-auto">
          {hasLeftPanel ? (
            <div className="grid grid-cols-1 tablet-lg:grid-cols-12 gap-8">
              <section className="tablet-lg:col-span-4 space-y-8">
                {/* Start a New Community */}
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-on-surface flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">edit_note</span>
                    {t('Start a New Community')}
                  </h3>
                  <CreateCommunityCard navigate={navigate} preset={preset} dense={dense} />
                </div>

                {invitations.length > 0 && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-bold text-on-surface flex items-center gap-2">
                      <span className="material-symbols-outlined text-secondary">mail</span>
                      {t('Invitations')}
                      <span className="bg-secondary text-on-secondary text-xs px-2 py-1 rounded-full">
                        {t('{{count}} New', { count: invitations.length })}
                      </span>
                    </h3>
                    <div className="space-y-6">
                      {invitations.map(inv => (
                        <InvitationCard
                          key={inv.id}
                          inv={inv}
                          acceptMutation={acceptMutation}
                          rejectMutation={rejectMutation}
                          dense={dense}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {availableCommunities.length > 0 && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-bold text-on-surface flex items-center gap-2">
                      <span className="material-symbols-outlined text-secondary">alternate_email</span>
                      {t('Available via Email')}
                    </h3>
                    <div className="space-y-6">
                      {availableCommunities.map(c => (
                        <AvailableCommunityCard
                          key={c.id}
                          c={c}
                          joinMutation={joinMutation}
                          preset={preset}
                          dense={dense}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </section>

              <section className="tablet-lg:col-span-8">
                <HeaderRow />
                {communitiesLoading ? <LoadingSkeleton /> : communities.length === 0 ? <EmptyState /> : (
                  <div className={sideGridClass}>
                    {communities.map((c, i) => (
                      <CommunityCard key={c.id} community={c} index={i} preset={preset} dense={dense} avatarMode={avatarMode} onClick={() => navigate(`/communities/${c.id}`)} />
                    ))}
                  </div>
                )}
              </section>
            </div>
          ) : (
            <>
              <HeaderRow />
              {communitiesLoading ? (
                <LoadingSkeleton cols="grid-cols-1 mobile-sm:grid-cols-2 tablet-lg:grid-cols-3 gap-6" />
              ) : communities.length === 0 ? (
                <EmptyState />
              ) : (
                <div className={fullGridClass}>
                  <CreateCommunityCard navigate={navigate} preset={preset} dense={dense} />
                  {communities.map((c, i) => (
                    <CommunityCard key={c.id} community={c} index={i} preset={preset} dense={dense} avatarMode={avatarMode} onClick={() => navigate(`/communities/${c.id}`)} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default Communities;
